import {
  Badge,
  Box,
  Flex,
  Icon,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';
import { Business, User, UserBusinessAssociation } from 'services/@types';

const roleColorMap: Record<string, string> = {
  owner: 'purple',
  seat_admin: 'blue',
  seat_contributor: 'teal',
  seat_hostess: 'pink',
  viewer: 'gray',
};

const statusColorMap: Record<string, string> = {
  active: 'green',
  removed: 'red',
};

interface BusinessAssociationsPanelProps {
  user: User;
  primaryBusiness: Business | null;
}

export default function BusinessAssociationsPanel({
  user,
  primaryBusiness,
}: BusinessAssociationsPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const brandColor = useColorModeValue('brand.500', 'brand.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const businesses = user.businesses || [];

  const getBusinessName = (assoc: UserBusinessAssociation) => {
    if (typeof assoc.businessID === 'object' && assoc.businessID) {
      return (assoc.businessID as Business).businessName;
    }
    return String(assoc.businessID);
  };

  const getBusinessId = (assoc: UserBusinessAssociation) => {
    if (typeof assoc.businessID === 'object' && assoc.businessID) {
      return (assoc.businessID as Business).id;
    }
    return String(assoc.businessID);
  };

  return (
    <Card p="30px">
      <Flex justify="space-between" align="center" mb="20px">
        <Text color={textColor} fontSize="xl" fontWeight="700">
          Business Associations
        </Text>
      </Flex>

      {/* Primary Business */}
      {primaryBusiness && (
        <Box mb="15px" p="12px" bg="brand.50" borderRadius="10px">
          <Flex justify="space-between" align="center">
            <Box>
              <Text fontSize="xs" color="gray.500" fontWeight="600">
                PRIMARY BUSINESS
              </Text>
              <Text fontSize="md" color={textColor} fontWeight="600">
                {primaryBusiness.businessName}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {primaryBusiness.email}
              </Text>
            </Box>
            <Icon
              as={FaEye}
              color={brandColor}
              cursor="pointer"
              onClick={() =>
                navigate(`/admin/businesses/detail/${primaryBusiness.id}`)
              }
              transition="all 0.2s linear"
              _hover={{ transform: 'scale(1.2)' }}
            />
          </Flex>
        </Box>
      )}

      {/* Businesses array */}
      {businesses.length > 0 ? (
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th borderColor={borderColor}>Business</Th>
              <Th borderColor={borderColor}>Role</Th>
              <Th borderColor={borderColor}>Status</Th>
              <Th borderColor={borderColor}>Added At</Th>
              <Th borderColor={borderColor}></Th>
            </Tr>
          </Thead>
          <Tbody>
            {businesses.map((assoc, idx) => (
              <Tr key={idx}>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm" fontWeight="500">
                    {getBusinessName(assoc)}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Badge
                    colorScheme={roleColorMap[assoc.role] || 'gray'}
                    fontSize="xs"
                    borderRadius="6px"
                  >
                    {assoc.role}
                  </Badge>
                </Td>
                <Td borderColor={borderColor}>
                  <Badge
                    colorScheme={statusColorMap[assoc.status] || 'gray'}
                    fontSize="xs"
                    borderRadius="6px"
                  >
                    {assoc.status}
                  </Badge>
                </Td>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm">
                    {assoc.addedAt
                      ? new Date(assoc.addedAt).toLocaleDateString()
                      : '-'}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Icon
                    as={FaEye}
                    color={brandColor}
                    cursor="pointer"
                    onClick={() =>
                      navigate(
                        `/admin/businesses/detail/${getBusinessId(assoc)}`,
                      )
                    }
                    transition="all 0.2s linear"
                    _hover={{ transform: 'scale(1.2)' }}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : (
        !primaryBusiness && (
          <Text color="gray.500" fontSize="sm">
            No business associations
          </Text>
        )
      )}
    </Card>
  );
}
